import styles from "./index.module.css";
import { useEffect, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { useGetStatesQuery, useNewClientMutation } from "../../services/api";
import { newClientSchema } from "../../yup";
import img from "../../assets/images/InceptionFinicallyLogo.png";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store/store";
import { tracker } from "../../services/mixpanel";
import SuccessModal from "./Success.modal";
import AuthSplitLayout from "../../components/AuthSplitLayout";

interface State {
  _id: string;
  state: string;
}
interface StateOption {
  label: string;
  value: string;
}
const isMobile = window.innerWidth <= 768 || window.innerHeight <= 768;

const getSelectStyles = (hasError: boolean) => ({
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "52px",
    borderRadius: "0.5rem",
    borderColor: hasError ? "#ef4444" : state.isFocused ? "#9CA3AF" : "#d1d5db",
    borderWidth: state.isFocused ? "2px" : "1px",
    boxShadow: "none",
    "&:hover": {
      borderColor: hasError ? "#ef4444" : "#d1d5db",
    },
    backgroundColor: "transparent",
    paddingLeft: "0.25rem",
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  menuList: (base: any) => ({
    ...base,
    maxHeight: 120,
    overflowY: "auto",
    fontSize: "14px",
  }),
  option: (base: any, state: any) => ({
    ...base,
    whiteSpace: "wrap",
    overflow: "hidden",
    fontSize: "14px",
    backgroundColor: state.isFocused ? "#f0f0f0" : "white",
    color: "black",
  }),
  valueContainer: (base: any) => ({
    ...base,
    paddingTop: "2px",
  }),
  input: (base: any) => ({
    ...base,
    "input:focus": {
      boxShadow: "none !important",
      outline: "none !important",
    },
  }),
});

const incomeOptions = [
  { label: "$250,000 - $1,000,000", value: "$250,000 - $1,000,000" },
  { label: "$1,000,000 - $5,000,000", value: "$1,000,000 - $5,000,000" },
  { label: "$5,000,000 - $10,000,000", value: "$5,000,000 - $10,000,000" },
  { label: "$10,000,000 +", value: "$10,000,000 +" },
];

const sourceOfIncomeOptions = [
  { label: "W2 Income", value: "W2 Income" },
  { label: "Sale of Stocks/Bonds", value: "Sale of Stocks/Bonds" },
  {
    label: "Sale of Company/Equity Event",
    value: "Sale of Company/Equity Event",
  },
  { label: "Other", value: "Other" },
];

const SignUpForm = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
    getValues,
  } = useForm({
    resolver: yupResolver(newClientSchema),
    mode: "onChange",
  });

  const navigate = useNavigate();
  const uniqueId = useAppSelector((state) => state.purlUser.uniqueId || "");
  const [newClient, { isLoading: loading }] = useNewClientMutation();
  const { data: states, isLoading, isSuccess } = useGetStatesQuery();
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
  const [stateOptions, setStateOptions] = useState<StateOption[]>([]);
  const hasStarted = useRef(false);
  const isSubmittedRef = useRef(false);

  const handleBeforeUnload = () => {
    // if form is already submitted
    if (isSubmittedRef.current) {
      tracker.newClient.userCreated(getValues()?.email);
      return;
    }

    //don't track form-abandoned event on page refresh
    const entries = performance.getEntriesByType("navigation");
    const navEntry =
      entries.length > 0 ? (entries[0] as PerformanceNavigationTiming) : null;
    const isReload = navEntry?.type === "reload";
    if (isReload) return;

    tracker.newClient.formAbandoned();
  };

  const handleBackToDashboard = () => {
    handleBeforeUnload();
    navigate(`/${uniqueId}`);
  };

  const onError = () => {
    tracker.newClient.formError();
  };

  const onSubmit = async (values: any) => {
    tracker.newClient.formSubmit(values.email);
    try {
      let income: string[] = [];
      values.sourceOfIncome.map((item: StateOption) => income.push(item.value));
      const sendData: INewClientReq = {
        First_Name: values.firstName,
        Last_Name: values.lastName,
        Email: values.email,
        Mobile: values.mobilePhone,
        State_of_Residence: values.state.label,
        Annual_Income: values.annualIncome.value,
        Primary_Source_of_Income: JSON.stringify(income),
        Is_an_Advisor: values.isAdvisor === "Yes",
        Contact_Type: "Client",
        Contact_Group: "Inception Financial",
        Lead_Source: "Website",
      };

      const result = await newClient(sendData).unwrap();
      if (!result.success) {
        toast.error(result.data.message);
      }
      if (result.success) {
        isSubmittedRef.current = true;
        setShowConfirmationMessage(true);
      }
    } catch (err: any) {
      toast.error(err.data.message);
      console.error("Error:", err);
    }
  };

  const getStates = () => {
    const data = states?.data || [];
    const stateOption: StateOption[] = data.map((item: State) => {
      return {
        label: item.state,
        value: item._id,
      };
    });
    setStateOptions(stateOption);
  };

  useEffect(() => {
    if (isSubmittedRef.current) {
      // when modal is closed
      if(!showConfirmationMessage)
        window.location.replace("https://www.inception.financial/");

      // auto redirect after 10 sec
      const timer = setTimeout(() => {
        window.location.replace("https://www.inception.financial/");
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [showConfirmationMessage]);

  useEffect(() => {
    isSuccess && getStates();
  }, [states]);

  useEffect(() => {
    if (isDirty && !hasStarted.current) {
      hasStarted.current = true;
      tracker.newClient.formStarted();
    }
  }, [isDirty]);

  useEffect(() => {
    tracker.newClient.pageView();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <AuthSplitLayout subtitle="Please fill in your details to consult with us.">
      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        className="w-full max-w-lg"
        noValidate
      >
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-[#1f2937] font-['Proxima_Nova']">
            Consult with Us
          </h2>
        </div>

        <div className="grid grid-cols-1 min-[1200px]:grid-cols-2 gap-4">
          <div>
            <div className="relative">
              <input
                type="text"
                autoComplete="off"
                placeholder=" "
                className={`peer w-full min-h-[52px] rounded-lg border bg-white px-4 py-0 text-base text-[#1f2937] outline-none focus:ring-0 focus:shadow-none focus:border-2 ${
                  errors.firstName ? "border-red-500" : "border-gray-300"
                }`}
                {...register("firstName")}
              />
              <label
                className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 bg-white px-1 text-base font-normal transition-all duration-200 ${
                  errors.firstName ? "text-red-500" : "text-[#9CA3AF]"
                } peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-sm peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:-translate-y-1/2 peer-[:not(:placeholder-shown)]:text-sm`}
              >
                First Name*
              </label>
            </div>
            {errors.firstName && (
              <span className="mt-0.5 block text-xs font-medium text-red-500">
                {errors.firstName?.message as string}
              </span>
            )}
          </div>

          <div>
            <div className="relative">
              <input
                type="text"
                autoComplete="off"
                placeholder=" "
                className={`peer w-full min-h-[52px] rounded-lg border bg-white px-4 py-0 text-base text-[#1f2937] outline-none focus:ring-0 focus:shadow-none focus:border-2 ${
                  errors.lastName ? "border-red-500" : "border-gray-300"
                }`}
                {...register("lastName")}
              />
              <label
                className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 bg-white px-1 text-base font-normal transition-all duration-200 ${
                  errors.lastName ? "text-red-500" : "text-[#9CA3AF]"
                } peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-sm peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:-translate-y-1/2 peer-[:not(:placeholder-shown)]:text-sm`}
              >
                Last Name*
              </label>
            </div>
            {errors.lastName && (
              <span className="mt-0.5 block text-xs font-medium text-red-500">
                {errors.lastName?.message as string}
              </span>
            )}
          </div>

          <div>
            <div className="relative">
              <input
                type="text"
                autoComplete="off"
                placeholder=" "
                className={`peer w-full min-h-[52px] rounded-lg border bg-white px-4 py-0 text-base text-[#1f2937] outline-none focus:ring-0 focus:shadow-none focus:border-2 ${
                  errors.mobilePhone ? "border-red-500" : "border-gray-300"
                }`}
                {...register("mobilePhone")}
              />
              <label
                className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 bg-white px-1 text-base font-normal transition-all duration-200 ${
                  errors.mobilePhone ? "text-red-500" : "text-[#9CA3AF]"
                } peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-sm peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:-translate-y-1/2 peer-[:not(:placeholder-shown)]:text-sm`}
              >
                Mobile Phone*
              </label>
            </div>
            {errors.mobilePhone && (
              <span className="mt-0.5 block text-xs font-medium text-red-500">
                {errors.mobilePhone?.message as string}
              </span>
            )}
          </div>

          <div>
            <div className="relative">
              <input
                type="email"
                autoComplete="off"
                placeholder=" "
                className={`peer w-full min-h-[52px] rounded-lg border bg-white px-4 py-0 text-base text-[#1f2937] outline-none focus:ring-0 focus:shadow-none focus:border-2 ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
                {...register("email")}
              />
              <label
                className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 bg-white px-1 text-base font-normal transition-all duration-200 ${
                  errors.email ? "text-red-500" : "text-[#9CA3AF]"
                } peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-sm peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:-translate-y-1/2 peer-[:not(:placeholder-shown)]:text-sm`}
              >
                Email Address*
              </label>
            </div>
            {errors.email && (
              <span className="mt-0.5 block text-xs font-medium text-red-500">
                {errors.email?.message as string}
              </span>
            )}
          </div>

          <div>
            <div className="relative">
              <Controller
                name="state"
                control={control}
                render={({ field }: any) => (
                  <Select
                    {...field}
                    placeholder=""
                    isLoading={isLoading}
                    options={stateOptions}
                    styles={getSelectStyles(!!errors.state)}
                    onChange={(selectedOption) =>
                      field.onChange(selectedOption)
                    }
                  />
                )}
              />
              <label
                className={`pointer-events-none absolute left-3 top-0 -translate-y-1/2 bg-white px-1 text-sm font-normal transition-all duration-200 ${
                  errors.state ? "text-red-500" : "text-[#9CA3AF]"
                }`}
              >
                State of Residence*
              </label>
            </div>
            {errors.state && (
              <span className="mt-0.5 block text-xs font-medium text-red-500">
                {errors.state?.message as string}
              </span>
            )}
          </div>

          <div>
            <div className="relative">
              <Controller
                name="annualIncome"
                control={control}
                render={({ field }: any) => (
                  <Select
                    {...field}
                    placeholder=""
                    isLoading={isLoading}
                    options={incomeOptions}
                    styles={getSelectStyles(!!errors.annualIncome)}
                    onChange={(selectedOption) =>
                      field.onChange(selectedOption)
                    }
                  />
                )}
              />
              <label
                className={`pointer-events-none absolute left-3 top-0 -translate-y-1/2 bg-white px-1 text-sm font-normal transition-all duration-200 ${
                  errors.annualIncome ? "text-red-500" : "text-[#9CA3AF]"
                }`}
              >
                Annual Income*
              </label>
            </div>
            {errors.annualIncome && (
              <span className="mt-0.5 block text-xs font-medium text-red-500">
                {errors.annualIncome?.message as string}
              </span>
            )}
          </div>
        </div>

        <div className="mt-4">
          <div className="relative">
            <Controller
              name="sourceOfIncome"
              control={control}
              render={({ field }: any) => (
                <Select
                  {...field}
                  placeholder=""
                  isLoading={isLoading}
                  options={sourceOfIncomeOptions}
                  styles={getSelectStyles(!!errors.sourceOfIncome)}
                  isMulti
                  onChange={(selectedOption) => field.onChange(selectedOption)}
                />
              )}
            />
            <label
              className={`pointer-events-none z-10 absolute left-3 top-0 -translate-y-1/2 bg-white px-1 text-sm font-normal transition-all duration-200 ${
                errors.sourceOfIncome ? "text-red-500" : "text-[#9CA3AF]"
              }`}
            >
              Primary Source of Income*
            </label>
          </div>
          {errors.sourceOfIncome && (
            <span className="mt-0.5 block text-xs font-medium text-red-500">
              {errors.sourceOfIncome?.message as string}
            </span>
          )}
        </div>

        <div className="mt-6">
          <p className="text-sm text-[#1f2937] font-['Proxima_Nova'] mb-3">
            I am an advisor or affiliated with an advisory firm:
          </p>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer text-[#1f2937] font-['Proxima_Nova']">
              <input
                type="radio"
                value="Yes"
                {...register("isAdvisor")}
                className="w-4 h-4 accent-[#4D5874]"
              />
              Yes
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-[#1f2937] font-['Proxima_Nova']">
              <input
                type="radio"
                value="No"
                {...register("isAdvisor")}
                className="w-4 h-4 accent-[#4D5874]"
              />
              No
            </label>
          </div>
          {errors.isAdvisor && (
            <span className="mt-0.5 block text-xs font-medium text-red-500">
              {errors.isAdvisor?.message as string}
            </span>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-[#4D5874] mt-8 relative flex w-full items-center justify-center rounded-[10px] h-[52px] min-h-[52px] px-4 text-base font-medium text-white transition-all duration-200 hover:bg-[#4D5874]/90 shadow-md disabled:opacity-70 font-['Proxima_Nova']"
        >
          <span className={loading ? "opacity-0" : ""}>Submit</span>
          {loading && (
            <span className="absolute h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          )}
        </button>

        <p className="mt-4 text-xs text-gray-500 font-['Proxima_Nova'] text-left">
          Already have an account?{" "}
          <button
            type="button"
            onClick={handleBackToDashboard}
            className="font-semibold text-[#3B755E] underline"
          >
            Login
          </button>
        </p>
      </form>

      <SuccessModal
        open={showConfirmationMessage}
        onClose={() => setShowConfirmationMessage(false)}
      />
    </AuthSplitLayout>
  );
};

export default SignUpForm;
